import { isValidElement, useMemo } from 'react'
import Button from '@/shared/Buttons/Button'
import Image from '@/shared/Common/Image'
import Table, { ITableField } from '@/shared/Tables/Table'
import useRegularTable from '@/shared/Tables/Table/ctrl/useRegularTable'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import confirmService from '@/services/ConfirmService/confirm.service'
import eventBus from '@/services/eventBus'
import EditIcon from '@/assets/icons/edit.svg?react'
import TrashIcon from '@/assets/icons/trash.svg?react'
import { CmsEntity, CmsLookupContext, CmsResourceConfig } from '../cms.types'
import { getAbsoluteAssetUrl } from '../cms.utils'
import css from '../cms.module.css'

interface IProps<T extends CmsEntity & Record<string, any>> {
  resource: CmsResourceConfig<T>
  items: T[]
  loading: boolean
  context: CmsLookupContext
  onEdit: (item: T) => void
  onDelete: (item: T) => Promise<boolean>
}

const CmsEntityTable = <T extends CmsEntity & Record<string, any>>(props: IProps<T>) => {
  const fields = useMemo<ITableField<T, any>[]>(() => {
    const dataFields = props.resource.columns.map((column) => ({
      key: column.key,
      header: column.header,
      width: column.width,
      getter: (item: T) => renderColumnValue(item, column.key, column.render(item, props.context), props.context),
      sortGetter: (item: T) => column.sortValue?.(item, props.context) ?? '',
    }))

    return [
      ...dataFields,
      {
        key: '__actions',
        header: '',
        width: '120px',
        getter: (item: T) => (
          <div className={css.actionsCell} onClick={(event) => event.stopPropagation()}>
            <Button size="small" type="default" icon={<EditIcon />} onClick={() => props.onEdit(item)} />
            <Button size="small" type="default" danger icon={<TrashIcon />} onClick={() => deleteWithConfirm(item)} />
          </div>
        ),
        sortGetter: () => '',
      },
    ]
  }, [props.resource, props.context])

  const table = useRegularTable({
    fields,
    itemKey: props.resource.idField as keyof T,
    items: props.items,
    tableKey: `cms/${props.resource.key}`,
  })

  const deleteWithConfirm = async (item: T) => {
    const id = props.resource.getEntityId ? props.resource.getEntityId(item) : item[props.resource.idField]
    const confirmation = await confirmService.requestConfirm({
      title: `Удалить ${props.resource.singularTitle}`,
      description: `ID: ${id}`,
      confirmText: 'Удалить',
      isDanger: true,
    })

    if (confirmation.response === 'confirm') {
      await props.onDelete(item)
    }
  }

  if (props.loading && props.items.length === 0) {
    return (
      <div className={css.emptyState}>
        <ThreeDotsLoader />
      </div>
    )
  }

  if (props.items.length === 0) {
    return (
      <div className={css.emptyState}>
        <p className="text-nm text-description">Нет записей</p>
      </div>
    )
  }

  return (
    <Table
      fields={table.fields as ITableField<T, keyof T>[]}
      items={table.orderedItems}
      itemKey={props.resource.idField as keyof T}
      loading={props.loading}
      totalItemsCount={table.totalItemsCount}
      pageNumber={table.pageNumber}
      itemsPerPage={table.itemsPerPage}
      orderingState={table.orderingState}
      onOrderingStateChange={table.onOrderingStateChange}
      onSwap={table.onSwap}
      resizePluginCtrl={table.resizePluginCtrl}
      onClick={props.onEdit}
    />
  )
}

const renderColumnValue = (
  item: Record<string, any>,
  key: string,
  value: unknown,
  context: CmsLookupContext
) => {
  if (['logo', 'image', 'url'].includes(key)) {
    const url = String(item[key] || '')
    const asset = context.assets.find((candidate) => candidate.url === url)
    const label = asset?.fileName || (url ? 'Изображение' : '')

    return (
      <div
        className={css.imageCell}
        onClick={(event) => {
          event.stopPropagation()
          if (url) {
            eventBus.pub('mediaPreview.request', {
              medias: [
                {
                  id: asset?.id || url,
                  fileName: asset?.fileName || url,
                  size: asset?.size || 0,
                  extension: asset?.extension || '',
                  dataType: asset?.dataType || 'image/*',
                  url: getAbsoluteAssetUrl(url),
                  createdAt: '',
                },
              ],
            })
          }
        }}
      >
        <div className={css.imagePreview}>
          {url && <Image alt={asset?.fileName || url} src={getAbsoluteAssetUrl(url)} style={{ width: '100%', height: '100%' }} />}
        </div>
        <span className="line-clamp">{label}</span>
      </div>
    )
  }

  if (typeof value === 'string') return value
  if (isValidElement(value)) return value
  return String(value ?? '')
}

export default CmsEntityTable
